import { toPlainText } from './markdown';
import { parseTags } from './tags';

export type NoteKind = 'text' | 'todo';
export type Role = 'owner' | 'editor' | 'viewer';

/** A note as stored locally (IndexedDB). */
export interface LocalNote {
  id: string;
  kind: NoteKind;
  title: string;
  /** Markdown content. */
  content: string;
  tags: string[];
  pinned: boolean;
  archived: boolean;
  deleted: boolean;
  /** Server version this note is based on (0 = never synced). */
  version: number;
  createdAt: number;
  updatedAt: number;
  ownerId: string;
  role: Role;
  shared: boolean;
  /** 1 if there are local changes that still need to be pushed. */
  dirty: 0 | 1;
  /** Incremented on every local edit; used to detect edits during a sync round trip. */
  localRev: number;
}

export type NoteDraft = Pick<LocalNote, 'title' | 'content'> & Partial<Pick<LocalNote, 'pinned' | 'archived' | 'kind'>>;

export const EMPTY_TODO = '- [ ] ';

/** Recomputes derived fields after a content change. */
export function withDerived<T extends Pick<LocalNote, 'title' | 'content'>>(note: T): T & { tags: string[] } {
  return { ...note, tags: parseTags(note.title, note.content) };
}

/** Whether a note can be edited by the current user. */
export function canEdit(note: Pick<LocalNote, 'role'>): boolean {
  return note.role !== 'viewer';
}

/** Whether the note has neither title nor content. */
export function isEmpty(note: Pick<LocalNote, 'title' | 'content'>): boolean {
  return note.title.trim() === '' && toPlainText(note.content).trim() === '';
}

/** Sort: pinned first, then most recently updated. */
export function compareNotes(a: LocalNote, b: LocalNote): number {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  return b.updatedAt - a.updatedAt;
}

/** Title or first line of content. */
export function displayTitle(note: Pick<LocalNote, 'title' | 'content'>): string {
  if (note.title.trim()) return note.title.trim();
  const first = toPlainText(note.content).split('\n')[0] ?? '';
  return first.trim().slice(0, 80);
}
